import React, { useState } from 'react';
import { Card, Grid, Modal, Fade, CardContent, CardHeader, IconButton, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import TitleBanner from '../../../../design-system/components/Banners/TitleBanner';
import Toast from '../../../../design-system/components/Toast/Toast';
import StudentSearch from './StudentSearch';
import ProcessCancel from './ProcessCancel';
import ExtendMembership from './ExtendMembership';
import ChangeMembership from './ChangeMembership';
import ResendEmail from './ResendEmail';
import useStudentSearch from './useStudentSearch';
import useModificationActions from './useModificationActions';
import useMembershipCatalogs from '../useMembershipCatalogs';
import { useB2BPermissions } from '../../../../context/B2BPermissionsContext';

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalCard: {
        width: '90%',
        maxWidth: 600,
        maxHeight: '80vh',
        overflowY: 'auto',
        outline: 'none',
    },
}));

const ACTION_TITLES = {
    baja: 'Procesar Baja',
    extension: 'Extender Membresía',
    cambio: 'Cambio de Membresía',
    reenvio_correo: 'Envío de Correo',
};

const ModificationsPage = () => {
    const classes = useStyles();
    const {
        students,
        loading,
        error,
        totalPages,
        page,
        setPage,
        searchStudents,
    } = useStudentSearch();

    const { loading: actionLoading, result, clearResult, processBaja, extendMembership, changeMembership, resendEmail } = useModificationActions();
    const { assignedCompanyName } = useB2BPermissions();

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [currentAction, setCurrentAction] = useState('');
    const [toastProps, setToastProps] = useState({ open: false, message: '', severity: 'success' });
    // Señal de recarga: se incrementa tras cada modificación exitosa para que
    // StudentSearch re-ejecute su última búsqueda (preservando filtros y empresa).
    const [refreshSignal, setRefreshSignal] = useState(0);

    // Dispara la PRECARGA de los catálogos (membresía/addon) al entrar a la
    // pantalla de Modificaciones, en lugar de esperar a que se abra el modal. El
    // hook cachea a nivel de módulo, así que cuando ChangeMembership lo use, los
    // datos ya estarán listos y el selector no aparecerá vacío. La llamada ocurre
    // una sola vez por sesión y se comparte con el alta de usuarios.
    useMembershipCatalogs();

    React.useEffect(() => {
        if (result) {
            setToastProps({ open: true, message: result.message, severity: result.type === 'success' ? 'success' : 'error' });
            if (result.type === 'success') {
                handleCloseModal();
                // Recargar los datos del alumno (baja/extensión/cambio) sin perder filtros.
                setRefreshSignal(prev => prev + 1);
            }
        }
    }, [result]);

    const handleSelectStudent = (student) => {
        setSelectedStudent(student);
        setCurrentAction(student.defaultAction || 'baja');
    };

    const handleCloseModal = () => {
        setSelectedStudent(null);
        setCurrentAction('');
    };

    const handleFinishedToast = () => {
        setToastProps(prev => ({ ...prev, open: false }));
        clearResult();
    };

    const companyTag = selectedStudent?.company_tag || assignedCompanyName[0] || '';

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TitleBanner
                        title="Membresías B2B - Modificaciones"
                        desc="Busque un alumno para ejecutar operaciones de baja, extensión o cambio."
                    />
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <Box p={2}>
                            <StudentSearch
                                students={students}
                                loading={loading}
                                error={error}
                                totalPages={totalPages}
                                page={page}
                                onSearch={searchStudents}
                                onPageChange={setPage}
                                onSelectStudent={handleSelectStudent}
                                refreshSignal={refreshSignal}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Modal de acciones */}
            <Modal
                open={Boolean(selectedStudent)}
                onClose={handleCloseModal}
                className={classes.modal}
                closeAfterTransition
                BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' } }}
            >
                <Fade in={Boolean(selectedStudent)}>
                    <Card className={classes.modalCard}>
                        <CardHeader
                            title={
                                <Typography variant="h3" style={{ fontWeight: 600 }}>
                                    {ACTION_TITLES[currentAction] || 'Acción'}
                                </Typography>
                            }
                            subheader={
                                selectedStudent && (
                                    <Box mt={1.5}>
                                        <Typography variant="h6" style={{ fontWeight: 500 }}>
                                            {selectedStudent.first_name} {selectedStudent.last_name}
                                        </Typography>
                                        <Typography variant="body1" style={{ color: '#7C4DFF' }}>
                                            {selectedStudent.email}
                                        </Typography>
                                    </Box>
                                )
                            }
                            action={
                                <IconButton onClick={handleCloseModal} disabled={actionLoading}>
                                    <CloseIcon />
                                </IconButton>
                            }
                        />
                        <CardContent>
                            {currentAction === 'baja' && selectedStudent && (
                                <ProcessCancel
                                    student={selectedStudent}
                                    loading={actionLoading}
                                    onConfirm={processBaja}
                                    disabled={actionLoading}
                                />
                            )}
                            {currentAction === 'extension' && selectedStudent && (
                                <ExtendMembership
                                    student={selectedStudent}
                                    loading={actionLoading}
                                    onConfirm={extendMembership}
                                    disabled={actionLoading}
                                />
                            )}
                            {currentAction === 'cambio' && selectedStudent && (
                                <ChangeMembership
                                    student={selectedStudent}
                                    loading={actionLoading}
                                    onConfirm={changeMembership}
                                    disabled={actionLoading}
                                    companyTag={companyTag}
                                />
                            )}
                            {currentAction === 'reenvio_correo' && selectedStudent && (
                                <ResendEmail
                                    student={selectedStudent}
                                    loading={actionLoading}
                                    onConfirm={resendEmail}
                                    disabled={actionLoading}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Fade>
            </Modal>

            <Toast {...toastProps} onFinished={handleFinishedToast} duration={5000} />
        </>
    );
};

export default ModificationsPage;
